import { styled, Menu, MenuItem, Divider, Box, useTheme, IconButton } from '@mui/material'
import { FC, memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CertificateFileItem } from 'src/generated-types'
import React from 'react'
import { useBatchNumberCalculations } from './BatchNumberCalculations'
import { DynamicTypography } from '../DynamicTextTypography'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { AddBatchNumberInline } from './AddBatchNumberInline'
import { AddCircleOutline } from '@mui/icons-material'

interface IBatchNumberCellProps {
  positionId: EntityId
  componentId: EntityId
  certificationFiles: CertificateFileItem[]
  onClick: (fileUrl: string) => void
  onAddSingleBatchNumber: (form: any) => void
  disabledAddBatchNumber?: boolean
}

export const BatchNumberCell: FC<IBatchNumberCellProps> = memo(
  ({ positionId, componentId, certificationFiles, onClick, onAddSingleBatchNumber, disabledAddBatchNumber }) => {
    const { t } = useTranslation(['position', 'common'])
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [isAddMenuOpen, setIsAddMenuOpen] = useState<boolean>(false)
    const [isHovered, setIsHovered] = useState<boolean>(false)

    const [forceUpdate, setForceUpdate] = useState(0)

    const { containerRef, getItemMaxWidth, moreButtonWidth, getOptimalDisplayCount, getIndividualItemWidth } =
      useBatchNumberCalculations({
        certificationFiles,
        forceUpdate,
      })

    const handleOpenDropdown = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    }, [])

    const handleCloseDropdown = useCallback(() => {
      setAnchorEl(null)
    }, [])

    const handleOpenAddMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setIsAddMenuOpen(true)
      setAnchorEl(event.currentTarget)
    }, [])

    const handleCloseAddMenu = useCallback(() => {
      setIsAddMenuOpen(false)
      setAnchorEl(null)
      setIsHovered(false)
    }, [])

    const handleItemClick = useCallback(
      (item: CertificateFileItem, isMenuItem: boolean) => {
        if (item?.url) {
          onClick(item.url)
          if (isMenuItem) {
            handleCloseDropdown()
          }
        }
      },
      [onClick, handleCloseDropdown],
    )

    const BatchNumberItem: FC<{
      item: CertificateFileItem
      index: number
      maxWidth?: number
      isMenuItem?: boolean
    }> = useCallback(
      ({ item, index, maxWidth, isMenuItem = false }) => {
        const batchNumber = item?.batchNumber || t('no-batch-numbers', { ns: 'position' })

        const handleClick = () => {
          handleItemClick(item, isMenuItem)
        }

        const content = isMenuItem ? (
          <StyledMenuItem onClick={item?.url ? handleClick : undefined} isClickable={!!item?.url}>
            <StyledDiv maxWidth={maxWidth || 200}>
              <DynamicTypography text={batchNumber} maxLength={maxWidth || 200} />
            </StyledDiv>
          </StyledMenuItem>
        ) : (
          <BatchNumberLableButton onClick={item?.url ? handleClick : undefined} disabled={!item?.url}>
            <StyledDiv maxWidth={maxWidth || 200}>
              <DynamicTypography text={batchNumber} maxLength={maxWidth || 200} />
            </StyledDiv>
          </BatchNumberLableButton>
        )

        return <Box key={index}>{content}</Box>
      },
      [t, handleItemClick],
    )

    const handleAddBatchNumber = useCallback(
      (data: any) => {
        onAddSingleBatchNumber(data)
        handleCloseAddMenu()

        setTimeout(() => {
          setForceUpdate(prev => prev + 1)
        }, 100)
      },
      [onAddSingleBatchNumber, handleCloseAddMenu],
    )

    const shouldShowAddButton = !disabledAddBatchNumber && (isHovered || isAddMenuOpen)

    if (certificationFiles.length === 0) {
      return (
        <Box ref={containerRef} display="flex" alignItems="center" width="100%" height="100%">
          <Box flexGrow={0}>
            <BatchNumberLableButton disabled={true}>
              <StyledDiv maxWidth={getItemMaxWidth()}>
                <DynamicTypography text={t('no-batch-numbers', { ns: 'position' })} maxLength={getItemMaxWidth()} />
              </StyledDiv>
            </BatchNumberLableButton>
          </Box>

          {!disabledAddBatchNumber && (
            <>
              <CustomDivider />
              <AddIconButton onClick={handleOpenAddMenu} size="small">
                <AddCircleOutline />
              </AddIconButton>
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={isAddMenuOpen}
            onClose={handleCloseAddMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <AddBatchNumberInline
              positionId={positionId}
              componentId={componentId}
              onSubmit={handleAddBatchNumber}
              batchNumbers={certificationFiles.map(file => file.batchNumber) as string[]}
              setMode={() => handleCloseAddMenu()}
            />
          </Menu>
        </Box>
      )
    }

    const optimalCount = getOptimalDisplayCount()
    const itemsToShow = Math.min(optimalCount, certificationFiles.length)
    const hasMore = certificationFiles.length > itemsToShow

    // When there are batch numbers
    return (
      <Box
        ref={containerRef}
        width="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box display="flex" alignItems="center" width="100%" justifyContent="flex-start">
          {certificationFiles.slice(0, itemsToShow).map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <CustomDivider />}
              <BatchNumberItem
                item={item}
                index={index}
                maxWidth={getIndividualItemWidth(itemsToShow, hasMore, shouldShowAddButton)}
              />
            </React.Fragment>
          ))}

          {hasMore && (
            <>
              <CustomDivider />
              <BatchNumberLableButton onClick={handleOpenDropdown}>
                <StyledDiv maxWidth={moreButtonWidth}>
                  {t('more', { ns: 'common' })
                    .replace(/\.\.\.$/, '')
                    .replace(/^./, str => str.toUpperCase())}
                </StyledDiv>
              </BatchNumberLableButton>
            </>
          )}

          {shouldShowAddButton && (
            <>
              <CustomDivider />
              <AddIconButton onClick={handleOpenAddMenu} size="small">
                <AddCircleOutline />
              </AddIconButton>
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && !isAddMenuOpen}
            onClose={handleCloseDropdown}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {certificationFiles.slice(itemsToShow).map((item, index) => (
              <BatchNumberItem key={index} item={item} index={index} maxWidth={250} isMenuItem={true} />
            ))}
          </Menu>

          <Menu
            anchorEl={anchorEl}
            open={isAddMenuOpen}
            onClose={handleCloseAddMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <AddBatchNumberInline
              positionId={positionId}
              componentId={componentId}
              onSubmit={handleAddBatchNumber}
              batchNumbers={certificationFiles.map(file => file.batchNumber) as string[]}
              setMode={() => handleCloseAddMenu()}
            />
          </Menu>
        </Box>
      </Box>
    )
  },
)

export const BatchNumberLableButton = styled('span')<{ disabled?: boolean }>`
  height: 32px;
  display: flex;
  align-items: center;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${props =>
    props.disabled
      ? getThemeColor(props.theme, MUIThemeColors.secondaryDark)
      : getThemeColor(props.theme, MUIThemeColors.primaryMain)};
  opacity: ${props => (props.disabled ? 0.9 : 1)};
  margin-top: 2px;
  padding: 4px 6px;
  transition: background 0.2s;
  border-radius: 4px;
  flex-shrink: 0;
  &:hover {
    background: ${props => (props.disabled ? 'none' : getThemeColor(props.theme, MUIThemeColors.secondaryLight))};
  }
`

export const AddIconButton = styled(IconButton)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  padding: 2,
  marginLeft: 2,
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  },
}))

export const StyledDiv = styled('div')<{ maxWidth: number }>(props => ({
  fontSize: 'inherit',
  maxWidth: `${props.maxWidth}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
}))

export const StyledMenuItem = styled(MenuItem)<{ isClickable?: boolean }>(({ theme, isClickable = true }) => ({
  color: isClickable
    ? getThemeColor(theme, MUIThemeColors.primaryMain)
    : getThemeColor(theme, MUIThemeColors.secondaryDark),
  cursor: isClickable ? 'pointer' : 'not-allowed',
}))

export const CustomDivider = styled('div')(({ theme }) => ({
  width: '1px',
  height: '20px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryMain) || getThemeColor(theme, MUIThemeColors.white),
  margin: '0 6px',
  flexShrink: 0,
  zIndex: 1300,
  position: 'relative',
}))

export const StyledDivider = styled(Divider)(({ theme }) => ({
  zIndex: 1300,
  position: 'relative',
  backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryMain),
  width: '1px !important',
  minHeight: '20px',
  opacity: 1,
  borderRightWidth: '1px',
  borderRightStyle: 'solid',
  borderRightColor: getThemeColor(theme, MUIThemeColors.secondaryMain),
}))
